import { Trans, useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';
import { Checkbox } from '../../../components/ui/checkbox';
import { Label } from '../../../components/ui/label';

interface ConsentCheckboxesProps {
  termsAccepted: boolean;
  contentProcessingConsent: boolean;
  onTermsChange: (checked: boolean) => void;
  onContentProcessingChange: (checked: boolean) => void;
  disabled?: boolean;
  termsError?: string;
  contentProcessingError?: string;
}

const PRIVACY_URL = 'https://www.panluma.ai/privacy-policy';
const TERMS_URL = 'https://www.panluma.ai/terms-of-service';

const PROVIDER_KEYS = [
  'disclosureProviderAnthropic',
  'disclosureProviderDeepgram',
  'disclosureProviderCartesia',
  'disclosureProviderSimli',
] as const;

/**
 * Two affirmative-consent checkboxes required to register. The disclosure
 * block above the second checkbox names the third-party providers and the
 * data categories sent — Apple App Review 5.1.1(i)/5.1.2(i) requires this
 * information to be visible during registration, not deferred to the
 * Privacy Policy alone.
 */
export function ConsentCheckboxes({
  termsAccepted,
  contentProcessingConsent,
  onTermsChange,
  onContentProcessingChange,
  disabled = false,
  termsError,
  contentProcessingError,
}: ConsentCheckboxesProps) {
  return (
    <div className="space-y-3 pt-1">
      <div className="flex items-start gap-2.5">
        <Checkbox
          id="terms_accepted"
          checked={termsAccepted}
          onCheckedChange={(checked) => onTermsChange(checked === true)}
          disabled={disabled}
          className="mt-0.5"
          data-testid="terms-checkbox"
        />
        <div className="flex-1">
          <Label htmlFor="terms_accepted" className="text-sm font-normal leading-snug cursor-pointer">
            <Trans
              i18nKey="auth:consent.termsLabel"
              components={{
                privacyLink: <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground" />,
                termsLink: <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground" />,
              }}
            />
          </Label>
          {termsError && <p className="text-xs text-destructive mt-1">{termsError}</p>}
        </div>
      </div>

      <DataProcessingDisclosure />

      <div className="flex items-start gap-2.5">
        <Checkbox
          id="content_processing_consent"
          checked={contentProcessingConsent}
          onCheckedChange={(checked) => onContentProcessingChange(checked === true)}
          disabled={disabled}
          className="mt-0.5"
          data-testid="content-processing-checkbox"
        />
        <div className="flex-1">
          <Label htmlFor="content_processing_consent" className="text-sm font-normal leading-snug cursor-pointer">
            <Trans i18nKey="auth:consent.contentProcessingLabel" />
          </Label>
          {contentProcessingError && <p className="text-xs text-destructive mt-1">{contentProcessingError}</p>}
        </div>
      </div>
    </div>
  );
}

function DataProcessingDisclosure() {
  const { t } = useTranslation('auth');

  return (
    <div className="rounded-md border border-border bg-muted/40 p-3 text-sm" data-testid="data-processing-disclosure">
      <div className="flex items-center gap-1.5 font-medium text-foreground">
        <Sparkles className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        <span>{t('consent.disclosureTitle')}</span>
      </div>
      <p className="mt-1.5 text-muted-foreground">{t('consent.disclosureIntro')}</p>
      <ul className="mt-2 space-y-1 text-muted-foreground">
        {PROVIDER_KEYS.map((key) => (
          <li key={key} className="flex gap-2">
            <span aria-hidden="true" className="mt-1.5 h-1 w-1 flex-shrink-0 rounded-full bg-muted-foreground/60" />
            <span className="leading-snug">
              <Trans i18nKey={`auth:consent.${key}`} components={{ b: <span className="font-medium text-foreground" /> }} />
            </span>
          </li>
        ))}
      </ul>
      <p className="mt-2.5 text-xs text-muted-foreground leading-snug">
        <Trans
          i18nKey="auth:consent.disclosureFooter"
          components={{
            privacyLink: <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground" />,
          }}
        />
      </p>
    </div>
  );
}
